// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { IconRefresh } from '@ttn-lw/components/icon'
import toast from '@ttn-lw/components/toast'
import Input from '@ttn-lw/components/input'
import Form, { useFormContext } from '@ttn-lw/components/form'
import ButtonGroup from '@ttn-lw/components/button/group'
import Button from '@ttn-lw/components/button'
import SubmitButton from '@ttn-lw/components/submit-button'

import Pill from '@console/components/pill/index.tti'
import { generateRandomHexColor } from '@console/components/pill/utils.tti'

import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import sharedMessages from '@ttn-lw/lib/shared-messages'
import PropTypes from '@ttn-lw/lib/prop-types'

import { createLabel, getLabelList } from '@console/store/actions/labels.tti'

import labelFormValidation from './label-form-validation.tti'

import style from './labels-table.tti.styl'

const InnerForm = ({ setIsCreating, randomColor, setRandomColor, setError }) => {
  const { setFieldValue, dirty, isValid, values } = useFormContext()
  const [previewText, setPreviewText] = useState('Label preview')
  const disableSubmit = !isValid || !dirty

  const handleClosePanel = useCallback(() => {
    setIsCreating(false)
    setError(undefined)
  }, [setIsCreating, setError])

  const handleGenerateRandomHexColor = useCallback(() => {
    const randomColor = generateRandomHexColor()
    setRandomColor(randomColor)
    setFieldValue('color', randomColor)
  }, [setFieldValue, setRandomColor])

  const handleColorChange = useCallback(
    value => {
      setRandomColor(value || '#fff')
    },
    [setRandomColor],
  )

  return (
    <>
      <Pill color={randomColor} text={previewText} />
      <div className="d-flex gap-cs-m al-start mt-cs-xxl md:direction-column">
        <Form.Field
          title={sharedMessages.labelName}
          placeholder={sharedMessages.labelName}
          name="name"
          component={Input}
          required
          autoFocus
          inputWidth="m"
          fieldWidth="xs"
          className="md:flex-basis-1"
          onChange={value => setPreviewText(Boolean(value) ? value : 'Label preview')}
          description={sharedMessages.labelId}
          descriptionValues={{ labelId: values.name.toLowerCase().split(' ').join('-') }}
        />
        <Form.Field
          title={sharedMessages.description}
          placeholder={sharedMessages.labelDescription}
          name="description"
          component={Input}
          inputWidth="m"
          fieldWidth="xs"
          className="md:flex-basis-1"
        />
        <div className="d-flex gap-cs-xs al-start">
          <Button
            icon={IconRefresh}
            type="button"
            secondary
            onClick={handleGenerateRandomHexColor}
            className={style.generateButton}
          />
          <Form.Field
            title={sharedMessages.color}
            placeholder={sharedMessages.labelColorPlaceholder}
            name="color"
            component={Input}
            required
            onChange={handleColorChange}
          />
        </div>
        <ButtonGroup align="end" className={style.buttonGroup}>
          <Button message={sharedMessages.cancel} onClick={handleClosePanel} secondary />
          <Form.Submit
            component={SubmitButton}
            message={sharedMessages.createLabel}
            disabled={disableSubmit}
          />
        </ButtonGroup>
      </div>
    </>
  )
}

InnerForm.propTypes = {
  randomColor: PropTypes.string.isRequired,
  setError: PropTypes.func.isRequired,
  setIsCreating: PropTypes.func.isRequired,
  setRandomColor: PropTypes.func.isRequired,
}

const CreateLabelPanel = ({ isCreating, setIsCreating }) => {
  const dispatch = useDispatch()
  const [error, setError] = useState(undefined)
  const [randomColor, setRandomColor] = useState(generateRandomHexColor())

  const initialValues = useMemo(
    () => ({
      ids: {
        label_id: '',
      },
      name: '',
      description: '',
      color: randomColor,
    }),
    [randomColor],
  )

  const handleSubmit = useCallback(
    async values => {
      values.ids.label_id = values.name.toLowerCase().split(' ').join('-')
      if (isCreating) {
        try {
          await dispatch(attachPromise(createLabel(values)))
          toast({
            title: values.name,
            message: sharedMessages.createLabelSuccess,
            type: toast.types.SUCCESS,
          })
          setIsCreating(false)
          setRandomColor(generateRandomHexColor())
          await dispatch(
            attachPromise(
              getLabelList({ order: '-created_at' }, ['name', 'description', 'entities', 'color']),
            ),
          )
          setError(undefined)
        } catch (err) {
          setError(err)
          toast({
            title: values.ids.label_id,
            message: sharedMessages.createLabelError,
            type: toast.types.ERROR,
          })
        }
      }
    },
    [isCreating, dispatch, setIsCreating],
  )

  if (!isCreating) {
    return null
  }

  return (
    <Form
      error={error}
      smallError
      initialValues={initialValues}
      validationSchema={labelFormValidation}
      onSubmit={handleSubmit}
      className={style.creatingPanel}
      validateOnChange
    >
      <InnerForm
        setIsCreating={setIsCreating}
        randomColor={randomColor}
        setRandomColor={setRandomColor}
        setError={setError}
      />
    </Form>
  )
}

CreateLabelPanel.propTypes = {
  isCreating: PropTypes.bool.isRequired,
  setIsCreating: PropTypes.func.isRequired,
}

export default CreateLabelPanel
